import { useCallback, useEffect, useState } from "react";
import { Box, Card, CardBody, CardHeader, Flex, Heading, Spinner, Tab, TabList, Tabs, Text } from "@chakra-ui/react";
import { DonutChart } from "@tremor/react";
import useApiRequest from "../../lib/hooks/useRequest";
import { waehlbareJahre } from "./waehlbare-jahre";
import {
  IPatientRequestListEntities,
  IPatientRequestStatistics,
  IPatientRequestStatisticsEntry,
  IPatientRequestStatusOption,
} from "../../lib/interfaces/patient-request";

// Haeuser haben, anders als der Status, keine Farbe im Backend - hier reicht eine feste
// Chart-Palette, in der Reihenfolge der Antwort durchgereicht.
const HAUS_FARBEN = ["#4AA5F5", "#6660a6", "#31b198", "#f59c19", "#e94360", "purple", "lime", "teal", "aqua", "maroon"];

const WAEHLBARE_JAHRE = waehlbareJahre(new Date().getFullYear());

/**
 * Die Auswertung der Patientenanfragen: Gesamtzahl, Status- und Haus-Verteilung fuer einen
 * waehlbaren Zeitraum (Issue KAN-232). Macht es wie die Bewerberstatistik in Home.tsx nach -
 * gleiche Donut-Darstellung mit Tremor, gleiches Null-statt-leer-Prinzip.
 */
export const PatientRequestsStatistics = () => {
  const { apiRequest } = useApiRequest();
  const [jahr, setJahr] = useState<string>("all");
  const [statusOptionen, setStatusOptionen] = useState<IPatientRequestStatusOption[]>([]);
  const [statistik, setStatistik] = useState<IPatientRequestStatistics>();
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    const laden = async () => {
      const entities = await apiRequest<IPatientRequestListEntities>("patient-requests/list-entities", "GET");
      if (entities.data) setStatusOptionen(entities.data.status);
    };
    laden();
  }, [apiRequest]);

  useEffect(() => {
    const laden = async () => {
      setLaedt(true);
      const query = jahr === "all" ? "" : `?year=${jahr}`;
      const antwort = await apiRequest<IPatientRequestStatistics>(`patient-requests/statistics${query}`, "GET");
      // Ohne eigenen Fehlerzustand bliebe die Karte bei einem Serverfehler einfach leer -
      // der Betrieb sieht dann nicht, ob es keine Anfragen gab oder ob etwas kaputt ist.
      setFehler(!antwort.data);
      if (antwort.data) setStatistik(antwort.data);
      setLaedt(false);
    };
    laden();
  }, [apiRequest, jahr]);

  const farbeFuerStatus = useCallback(
    (eintrag: IPatientRequestStatisticsEntry): string => statusOptionen.find((option) => option.id === eintrag._id)?.color ?? "#A0AEC0",
    [statusOptionen]
  );

  return (
    <Card marginBottom="1.5em">
      <CardHeader>
        <Flex justify="space-between" align="center" wrap="wrap" gap="2">
          <Heading size="md">Auswertung</Heading>
          <Tabs size="sm" variant="soft-rounded" onChange={(index) => setJahr(WAEHLBARE_JAHRE[index])}>
            <TabList>
              {WAEHLBARE_JAHRE.map((eintrag) => (
                <Tab key={eintrag} sx={{ color: "#7C8080" }}>
                  {eintrag === "all" ? "Gesamt" : eintrag}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </Flex>
      </CardHeader>
      <CardBody>
        {laedt ? (
          <Spinner />
        ) : fehler && !statistik ? (
          <Text fontSize="sm" color="gray.500">
            Die Auswertung konnte gerade nicht geladen werden. Lade die Seite neu.
          </Text>
        ) : statistik && statistik.total === 0 ? (
          // Vier Nullen und zwei leere Kreise sagen weniger als ein Satz. Wer einen
          // Zeitraum ohne Anfragen waehlt, soll das lesen koennen statt es zu deuten.
          <Text fontSize="sm" color="gray.500">
            {jahr === "all" ? "Bisher ist keine Anfrage eingegangen." : `Im Jahr ${jahr} ist keine Anfrage eingegangen.`}
          </Text>
        ) : statistik ? (
          <Flex gap="10" wrap="wrap" align="flex-start">
            <Box>
              <Text fontSize="sm" color="gray.500">
                Anfragen gesamt
              </Text>
              <Text fontSize="3xl" fontWeight="bold">
                {statistik.total}
              </Text>
              {/* Geloeschte und nach sechs Monaten automatisch entfernte Anfragen zaehlen
                  weiter mit, sonst waere der Jahresrueckblick irgendwann leer. Ohne diese
                  Zeile liest sich der Unterschied zur Liste wie ein Fehler. */}
              {statistik.stillPresent < statistik.total && (
                <Text fontSize="sm" color="gray.500">
                  eingegangen, {statistik.stillPresent} noch in der Liste
                </Text>
              )}
            </Box>
            <StatistikDonut titel="Status" daten={statistik.byStatus} farben={statistik.byStatus.map(farbeFuerStatus)} />
            <StatistikDonut titel="Standorte" daten={statistik.byLocation} farben={HAUS_FARBEN} />
          </Flex>
        ) : null}
      </CardBody>
    </Card>
  );
};

const StatistikDonut = ({ titel, daten, farben }: { titel: string; daten: IPatientRequestStatisticsEntry[]; farben: string[] }) => {
  const formatiert = daten.map((eintrag) => ({ title: eintrag.title ?? "Nicht zugewiesen", count: eintrag.count }));

  return (
    <div className="flex items-center gap-4">
      <DonutChart
        label={titel}
        data={formatiert}
        category="count"
        index="title"
        className="min-w-32 w-32 font-semibold"
        colors={farben}
        noDataText="0"
      />
      <div className="flex flex-col break-all">
        {formatiert.map((eintrag, index) => (
          <div key={eintrag.title + index} className="flex items-center gap-2">
            <div className="font-bold text-lg text-right">{eintrag.count}</div>
            <div className="text-sm font-semibold">
              <p
                className="overflow-hidden"
                style={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}
              >
                {eintrag.title}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientRequestsStatistics;
